import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm, Controller, useWatch } from 'react-hook-form';
import {
	Autocomplete,
	Button,
	CircularProgress,
	Divider,
	OutlinedInput,
	Stack,
	TextField,
} from '@mui/material';
import Box from '@mui/material/Box';
import DrawerButton from '../../../DrawerButton';
import DrawerBody from '../../../DrawerBody';
import DrawerMessage from '../../../DrawerMessage';
import DrawerFooter from '../../../DrawerFooter';
import DrawerHeader from '../../../DrawerHeader';
import { renderablePropType } from '../../../../utils/propTypes';
import DatePickerField from '../../../DatePickerField';
import FormFieldWrapper from '../../../FormFieldWrapper';
import ProductsSummary from './ProductsSummary';
import OrderSummary from './OrderSummary';
import NewOrderConfirmationModal from '../NewOrderConfirmationModal';
import { useSessionState } from '../../../../hooks/useSessionState';
import { useUserEmail } from '../../../../authentication/useAuth';
import ResetButton from '../../../DrawerButton/ResetButton';
import Section from '../../../DrawerButton/Section';
import {
	LIVE_LIST_NAMES,
	useLiveListApi,
	useOrderRequestsApi,
} from '../../../../apis';
import { LiveListFilters } from '../../../../apis/hooks/models/liveListFilters';
import { fetchShipToAddresses as fetchShipToAddressesLiveList } from '../../../../services/shipToAddresses';
import {
	defaultNewOrderValues,
	getProductOptionLabel,
	getShipToAddressOptionLabel,
	hydrateFormValues,
	serializeFormValues,
	toProductOptions,
} from './helpers';

function NewOrderDrawer({
	title,
	canOrder = false,
	ButtonProps = {},
	dataTestId,
	...props
}) {
	const { t } = useTranslation();
	const userEmail = useUserEmail() || '';
	const { get: getLiveList, cancel: cancelLiveList } = useLiveListApi();
	const { get: getShipToAddresses, cancel: cancelShipToAddresses } =
		useLiveListApi();
	const { submitOrderRequest } = useOrderRequestsApi();
	const [isOpen, setIsOpen] = useState(false);
	const [isSubmittingRequest, setIsSubmittingRequest] = useState(false);
	const [isLoadingProducts, setIsLoadingProducts] = useState(false);
	const [isLoadingShipToAddresses, setIsLoadingShipToAddresses] =
		useState(false);
	const [productOptions, setProductOptions] = useState([]);
	const [shipToAddressOptions, setShipToAddressOptions] = useState([]);
	const [confirmation, setConfirmation] = useState(null);
	const [submitOrderError, setSubmitOrderError] = useState('');
	const productRequestRef = useRef(0);
	const selectedProductOptionRef = useRef(null);
	const selectedShipToAddressOptionRef = useRef(null);
	const [storedFormValues, setStoredFormValues] = useSessionState(
		'orders_new_orders_form',
		serializeFormValues(defaultNewOrderValues)
	);
	const [items, setItems] = useSessionState('orders_new_orders_items', []);
	const initialFormValues = useMemo(
		() => hydrateFormValues(storedFormValues),
		[storedFormValues]
	);

	const middleMinHeight = 380;

	// Order Summary Items
	const totalQuantity = items.reduce(
		(sum, item) => sum + Number(item.quantity || 0),
		0
	);
	const totalProductCount = items.length;

	const { control, handleSubmit, reset, getValues } = useForm({
		defaultValues: initialFormValues,
	});
	const watchedFormValues = useWatch({ control });
	const productSearch = watchedFormValues?.product?.trim() || '';
	const shipToAddressSearch = watchedFormValues?.shipToAddress?.trim() || '';

	// ----------------------------------
	// Fetch Ship-To Addresses on mount
	// ----------------------------------
	useEffect(() => {
		const fetchShipToAddresses = async () => {
			setIsLoadingShipToAddresses(true);

			const response = await fetchShipToAddressesLiveList(getShipToAddresses);

			if (response?.isAborted) {
				setIsLoadingShipToAddresses(false);
				return;
			}

			if (response?.isError) {
				console.error(
					response?.error?.message ||
						t('new_orders:errors:load_ship_to_addresses')
				);
				setShipToAddressOptions([]);
				setIsLoadingShipToAddresses(false);
				return;
			}

			setShipToAddressOptions(response?.options || []);
			setIsLoadingShipToAddresses(false);
		};

		fetchShipToAddresses();

		return () => {
			cancelShipToAddresses();
		};
	}, [cancelShipToAddresses, getShipToAddresses, t]);

	useEffect(() => {
		if (!watchedFormValues?.shipToAddress) {
			selectedShipToAddressOptionRef.current = null;
			return;
		}

		selectedShipToAddressOptionRef.current =
			shipToAddressOptions.find(
				(option) =>
					getShipToAddressOptionLabel(option) ===
					watchedFormValues.shipToAddress
			) || null;
	}, [shipToAddressOptions, watchedFormValues?.shipToAddress]);

	// ---------------------------------------------------------
	// Persist form progress to session storage so drawer input
	// survives closes, reloads, and navigation.
	// ---------------------------------------------------------
	useEffect(() => {
		if (!watchedFormValues) {
			return;
		}

		setStoredFormValues(serializeFormValues(watchedFormValues));
	}, [watchedFormValues, setStoredFormValues]);

	useEffect(() => {
		if (!isOpen || !productSearch) {
			cancelLiveList();
			setProductOptions([]);
			setIsLoadingProducts(false);
			return undefined;
		}

		const requestId = productRequestRef.current + 1;
		productRequestRef.current = requestId;

		const timeoutId = window.setTimeout(async () => {
			setIsLoadingProducts(true);

			const response = await getLiveList({
				listName: LIVE_LIST_NAMES.PRODUCTS_BY_CUSTOMER_ITEM_NUMBER,
				filters: new LiveListFilters({ searchTerm: productSearch }),
			});

			if (productRequestRef.current !== requestId) {
				return;
			}

			if (response?.isAborted) {
				setIsLoadingProducts(false);
				return;
			}

			if (response?.isError) {
				console.error(
					response?.error?.message ||
						t('new_orders:errors:load_product_suggestions')
				);
				setProductOptions([]);
				setIsLoadingProducts(false);
				return;
			}

			setProductOptions(toProductOptions(response?.data));
			setIsLoadingProducts(false);
		}, 300);

		return () => {
			window.clearTimeout(timeoutId);
			cancelLiveList();
		};
	}, [cancelLiveList, isOpen, getLiveList, productSearch, t]);

	useEffect(
		() => () => {
			cancelLiveList();
		},
		[cancelLiveList]
	);

	const onOpen = () => {
		if (!canOrder) {
			return;
		}

		setIsOpen(true);
	};

	const onClose = () => {
		setIsOpen(false);
		cancelLiveList();
		setProductOptions([]);
		setIsLoadingProducts(false);
		setSubmitOrderError('');
		productRequestRef.current += 1;
		selectedProductOptionRef.current = null;
		selectedShipToAddressOptionRef.current = null;
	};

	const onClearOrders = () => {
		if (!canOrder) {
			return;
		}

		reset(defaultNewOrderValues);
		setStoredFormValues(serializeFormValues(defaultNewOrderValues));
		setItems([]);
		cancelLiveList();
		setProductOptions([]);
		setSubmitOrderError('');
		productRequestRef.current += 1;
		selectedProductOptionRef.current = null;
		selectedShipToAddressOptionRef.current = null;
	};

	const onDeleteItem = (itemValue) => {
		if (!canOrder) {
			return;
		}

		setItems((prev) => prev.filter((item) => item.value !== itemValue));
	};

	const onSubmit = (data) => {
		if (!canOrder) {
			return;
		}

		const normalized = {
			label: selectedProductOptionRef.current?.label || '',
			value: selectedProductOptionRef.current?.value || '',
			designNumber:
				selectedProductOptionRef.current?.metadata?.designNumber || '',
			quantity: Number(data.quantity),
		};

		setItems((prev) => {
			const existingItemIndex = prev.findIndex(
				(item) => item.value === normalized.value
			);

			if (existingItemIndex === -1) {
				return [...prev, normalized];
			}

			return prev.map((item, index) =>
				index === existingItemIndex
					? {
							...item,
							quantity: Number(item.quantity || 0) + normalized.quantity,
					  }
					: item
			);
		});

		const nextFormValues = {
			po: data.po,
			dueDate: data.dueDate,
			product: '',
			quantity: '',
			shipToAddress: data.shipToAddress,
		};

		selectedProductOptionRef.current = null;
		reset(nextFormValues);
		setStoredFormValues(serializeFormValues(nextFormValues));
	};

	const saveOrderRequests = async () => {
		if (!canOrder || items.length === 0 || isSubmittingRequest) {
			return;
		}

		const formValues = getValues();
		const selectedShipToAddressOption =
			selectedShipToAddressOptionRef.current ||
			shipToAddressOptions.find(
				(option) =>
					getShipToAddressOptionLabel(option) === formValues?.shipToAddress
			);
		const payload = {
			PoNumber: formValues?.po || '',
			OrderDueDate: formValues?.dueDate
				? new Date(formValues.dueDate).toISOString()
				: null,
			plantCustomerId: selectedShipToAddressOption?.value || '',
			Items: items.map((item) => ({
				ProductGuid: item.id || item.value,
				Quantity: Number(item.quantity || 0),
			})),
			Requester: userEmail,
		};

		setIsSubmittingRequest(true);
		setSubmitOrderError('');

		const response = await submitOrderRequest(payload);

		if (response?.isAborted) {
			setIsSubmittingRequest(false);
			return;
		}

		if (response?.isError) {
			setSubmitOrderError(response?.error?.message || t('error'));
			setIsSubmittingRequest(false);
			return;
		}

		setConfirmation({
			referencePo: formValues?.po || '',
			totalQuantity: items.reduce(
				(sum, item) => sum + Number(item.quantity || 0),
				0
			),
			totalProductCount: items.length,
			items: items.map((item, index) => ({
				id: item.value,
				itemNumber: String(index + 1).padStart(4, '0'),
				value: item.value,
				label: item.label,
				designNumber: item.designNumber,
				quantity: item.quantity,
			})),
		});
		setIsOpen(false);
		reset(defaultNewOrderValues);
		setStoredFormValues(serializeFormValues(defaultNewOrderValues));
		setItems([]);
		selectedProductOptionRef.current = null;
		selectedShipToAddressOptionRef.current = null;
		setIsSubmittingRequest(false);
	};

	return (
		<>
			<DrawerButton
				buttonText={title || t('new_orders:button_open')}
				isOpen={isOpen}
				onOpen={onOpen}
				onClose={onClose}
				ButtonProps={{
					...{ 'data-testid': dataTestId || 'new_order_drawer' },
					variant: 'contained',
					disabled: !canOrder,
					...ButtonProps,
				}}
				DrawerProps={{
					PaperProps: {
						sx: {
							width: 800,
							maxWidth: '100vw',
						},
					},
				}}
				{...props}
			>
				<DrawerHeader onClose={onClose} title={t('new_orders:title')}>
					<ResetButton onClick={onClearOrders} disabled={!canOrder}>
						{t('new_orders:button_clear_orders')}
					</ResetButton>
				</DrawerHeader>

				<DrawerMessage message={submitOrderError} severity="error" />

				<DrawerBody
					sx={{
						width: '100%',
						px: 0,
						pb: 0,
						overflow: 'hidden',
					}}
				>
					<Box
						sx={{
							flex: 1,
							minHeight: 0,
							overflowY: 'auto',
							overflowX: 'hidden',
							scrollbarGutter: 'stable',
							pl: 3,
							pr: 2,
							pb: 1,
						}}
					>
						<Box sx={{ flexShrink: 0 }}>
							<form onSubmit={handleSubmit(onSubmit)} noValidate>
								<Section isFirst>
									{/* PO */}
									<Controller
										name="po"
										control={control}
										rules={{ required: t('validation:required') }}
										render={({ field, fieldState }) => (
											<FormFieldWrapper
												label={t('new_orders:fields:po_number')}
												disabled={!canOrder}
												error={!!fieldState.error}
												helperText={fieldState.error?.message}
											>
												<OutlinedInput
													{...field}
													disabled={!canOrder}
													size="small"
													color="primary"
													margin="dense"
												/>
											</FormFieldWrapper>
										)}
									/>

									{/* Due Date */}
									<Controller
										name="dueDate"
										control={control}
										rules={{ required: t('validation:required') }}
										render={({ field, fieldState }) => (
											<FormFieldWrapper
												label={t('new_orders:fields:due_date')}
												disabled={!canOrder}
												error={!!fieldState.error}
												helperText={fieldState.error?.message}
											>
												<DatePickerField
													value={field.value ?? null}
													onChange={field.onChange}
													disabled={!canOrder}
												/>
											</FormFieldWrapper>
										)}
									/>

									<Controller
										name="shipToAddress"
										control={control}
										rules={{
											required: t('validation:required'),
											validate: () =>
												selectedShipToAddressOptionRef.current?.value
													? true
													: t('validation:required'),
										}}
										render={({ field, fieldState }) => (
											<FormFieldWrapper
												label={t('new_orders:fields:ship_to_address')}
												disabled={!canOrder}
												error={!!fieldState.error}
												helperText={fieldState.error?.message}
											>
												<Autocomplete
													disabled={!canOrder}
													freeSolo
													options={shipToAddressOptions}
													getOptionLabel={getShipToAddressOptionLabel}
													getOptionDisabled={(option) =>
														Boolean(option?.disabled)
													}
													inputValue={field.value || ''}
													onInputChange={(_, newInputValue) => {
														field.onChange(newInputValue);

														if (
															getShipToAddressOptionLabel(
																selectedShipToAddressOptionRef.current
															) !== newInputValue
														) {
															selectedShipToAddressOptionRef.current = null;
														}
													}}
													onChange={(_, newValue) => {
														selectedShipToAddressOptionRef.current =
															typeof newValue === 'string' ? null : newValue;
														field.onChange(
															getShipToAddressOptionLabel(newValue)
														);
													}}
													loading={isLoadingShipToAddresses}
													isOptionEqualToValue={(option, value) =>
														option?.value === value?.value
													}
													noOptionsText={
														shipToAddressSearch
															? t(
																	'new_orders:autocomplete:no_ship_to_addresses'
															  )
															: t(
																	'new_orders:autocomplete:start_typing_to_search'
															  )
													}
													renderInput={(params) => {
														const textFieldParams = {
															...params,
															inputProps: {
																...params.inputProps,
																'aria-label': t(
																	'new_orders:fields:ship_to_address'
																),
															},
															InputProps: {
																...params.InputProps,
																endAdornment: (
																	<>
																		{isLoadingShipToAddresses ? (
																			<CircularProgress
																				color="inherit"
																				size={18}
																			/>
																		) : null}
																		{params.InputProps.endAdornment}
																	</>
																),
															},
														};

														return (
															<TextField
																{...textFieldParams}
																size="small"
																color="primary"
																margin="dense"
																error={!!fieldState.error}
															/>
														);
													}}
												/>
											</FormFieldWrapper>
										)}
									/>

									<Divider sx={{ mt: 2, mb: 2 }} />

									{/* Product */}
									<Controller
										name="product"
										control={control}
										rules={{
											required: t('validation:required'),
											validate: () =>
												selectedProductOptionRef.current?.value
													? true
													: t('validation:required'),
										}}
										render={({ field, fieldState }) => (
											<FormFieldWrapper
												label={t('new_orders:fields:product')}
												disabled={!canOrder}
												error={!!fieldState.error}
												helperText={fieldState.error?.message}
											>
												<Autocomplete
													disabled={!canOrder}
													freeSolo
													options={productOptions}
													filterOptions={(options) => options}
													getOptionLabel={getProductOptionLabel}
													getOptionDisabled={(option) =>
														Boolean(option?.disabled)
													}
													inputValue={field.value || ''}
													onInputChange={(_, newInputValue) => {
														field.onChange(newInputValue);

														if (
															getProductOptionLabel(
																selectedProductOptionRef.current
															) !== newInputValue
														) {
															selectedProductOptionRef.current = null;
														}
													}}
													onChange={(_, newValue) => {
														selectedProductOptionRef.current =
															typeof newValue === 'string' ? null : newValue;
														field.onChange(getProductOptionLabel(newValue));
													}}
													loading={isLoadingProducts}
													isOptionEqualToValue={(option, value) =>
														option?.value === value?.value
													}
													noOptionsText={
														productSearch
															? t('new_orders:autocomplete:no_products_found')
															: t(
																	'new_orders:autocomplete:start_typing_to_search'
															  )
													}
													renderInput={(params) => {
														const textFieldParams = {
															...params,
															inputProps: {
																...params.inputProps,
																'aria-label': t('new_orders:fields:product'),
															},
															InputProps: {
																...params.InputProps,
																endAdornment: (
																	<>
																		{isLoadingProducts ? (
																			<CircularProgress
																				color="inherit"
																				size={18}
																			/>
																		) : null}
																		{params.InputProps.endAdornment}
																	</>
																),
															},
														};

														return (
															<TextField
																{...textFieldParams}
																size="small"
																color="primary"
																margin="dense"
																error={!!fieldState.error}
															/>
														);
													}}
												/>
											</FormFieldWrapper>
										)}
									/>

									{/* Quantity */}
									<Controller
										name="quantity"
										control={control}
										rules={{
											required: t('validation:required'),
											validate: (value) =>
												/^\d+$/.test(value)
													? true
													: t('validation:greater_than_equal_zero'),
										}}
										render={({ field, fieldState }) => (
											<FormFieldWrapper
												label={t('new_orders:fields:quantity')}
												disabled={!canOrder}
												error={!!fieldState.error}
												helperText={fieldState.error?.message}
											>
												<OutlinedInput
													{...field}
													disabled={!canOrder}
													size="small"
													color="primary"
													margin="dense"
													inputProps={{
														inputMode: 'numeric',
														pattern: '[0-9]*',
													}}
													onChange={(e) => {
														const numericValue = e.target.value.replace(
															/\D/g,
															''
														);
														field.onChange(numericValue);
													}}
												/>
											</FormFieldWrapper>
										)}
									/>
								</Section>
								<Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
									{/* Submit button */}
									<Button
										type="submit"
										variant="contained"
										disabled={!canOrder}
									>
										{t('new_orders:button_add_products')}
									</Button>
								</Stack>
							</form>
						</Box>

						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								minHeight: middleMinHeight,
								pb: 4,
								mb: 4,
							}}
						>
							<ProductsSummary
								items={items}
								onDelete={onDeleteItem}
								disabled={!canOrder}
							/>
							{items.length > 0 && (
								<OrderSummary
									totalQuantity={totalQuantity}
									totalProductCount={totalProductCount}
								/>
							)}
						</Box>
					</Box>
				</DrawerBody>

				<DrawerFooter divider>
					<Stack direction="row" justifyContent="center">
						<Button
							variant="contained"
							disabled={!canOrder || items.length === 0 || isSubmittingRequest}
							onClick={saveOrderRequests}
						>
							{isSubmittingRequest
								? t('new_orders:button_submitting')
								: t('common:submit')}
						</Button>
					</Stack>
				</DrawerFooter>
			</DrawerButton>

			<NewOrderConfirmationModal
				open={Boolean(confirmation)}
				confirmation={confirmation}
				onClose={() => setConfirmation(null)}
			/>
		</>
	);
}

NewOrderDrawer.propTypes = {
	title: renderablePropType,
	canOrder: PropTypes.bool,
	ButtonProps: PropTypes.object,
	dataTestId: PropTypes.string,
};

export default NewOrderDrawer;
